import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { startOfDay, endOfDay } from "date-fns";

export const useMobileNavBadges = () => {
  const { user } = useAuth();

  const { data: inboxCount = 0 } = useQuery({
    queryKey: ["mobile-nav-inbox-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      // Count pending tasks (not done, not archived)
      const { count: tasksCount } = await supabase
        .from("tasks")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .neq("status", "done")
        .eq("is_archived", false)
        .is("deleted_at", null);

      // Count active sales leads (not closed stages)
      const { count: leadsCount } = await supabase
        .from("sales_leads")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_archived", false)
        .is("deleted_at", null)
        .not("stage", "in", '("won","lost")');

      // Count pending invoices (draft, sent, overdue)
      const { count: invoicesCount } = await supabase
        .from("invoices")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .is("deleted_at", null)
        .in("status", ["draft", "sent", "overdue"]);

      return (tasksCount || 0) + (leadsCount || 0) + (invoicesCount || 0);
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const { data: calendarTodayCount = 0 } = useQuery({
    queryKey: ["mobile-nav-calendar-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const today = new Date();
      const dayStart = startOfDay(today).toISOString();
      const dayEnd = endOfDay(today).toISOString();

      const { count } = await supabase
        .from("calendar_events")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("status", "scheduled")
        .gte("start_time", dayStart)
        .lte("start_time", dayEnd);

      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  return {
    inboxCount,
    calendarTodayCount,
  };
};
